import { useState, useEffect, lazy, Suspense } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, getAppId } from './lib/firebase';

// Lazy loading Views for performance
const DashboardView = lazy(() => import('./views/Recruiter/Dashboard').then(m => ({ default: m.DashboardView })));
const JobCreatorView = lazy(() => import('./views/Recruiter/JobCreator').then(m => ({ default: m.JobCreatorView })));
const JobDetailView = lazy(() => import('./views/Recruiter/JobDetailView').then(m => ({ default: m.JobDetailView })));
const CandidateAssessmentView = lazy(() => import('./views/Candidate/CandidateAssessmentView/index').then(m => ({ default: m.CandidateAssessmentView })));
const ApprovalView = lazy(() => import('./views/Recruiter/ApprovalView').then(m => ({ default: m.ApprovalView })));
const MagicOnboardingView = lazy(() => import('./views/Recruiter/MagicOnboardingView').then(m => ({ default: m.MagicOnboardingView })));
const LeadView = lazy(() => import('./views/LeadView').then(m => ({ default: m.LeadView })));
const LoginView = lazy(() => import('./views/LoginView').then(m => ({ default: m.LoginView })));
const BusinessDashboard = lazy(() => import('./views/Admin/BusinessDashboard').then(m => ({ default: m.BusinessDashboard })));
const PublicDossierView = lazy(() => import('./views/PublicDossierView').then(m => ({ default: m.PublicDossierView })));

import { callBackendFunction } from './api/backend';

// Components & Assets
import { LoadingIndicator } from './components/ui/LoadingIndicator';
import { NotificationToast } from './components/ui/NotificationToast';

export default function App() {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('dashboard');
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [showOnboarding, setShowOnboarding] = useState(false);

    // --- ADMIN & MANAGED SERVICE STATE ---
    const [adminClients, setAdminClients] = useState([]);
    const [effectiveUserId, setEffectiveUserId] = useState(null);
    const [companyData, setCompanyData] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        // 1. Public Routes
        if (params.get('view') === 'assess') {
            setView('candidate_assess');
            setLoading(false);
            return;
        }
        if (params.get('view') === 'approve') {
            setView('approve');
            setLoading(false);
            return;
        }
        if (params.get('view') === 'dossier') {
            setView('dossier');
            setLoading(false);
            return;
        }

        // 2. Auth Listener
        const unsubscribe = auth.onAuthStateChanged(async (u) => {
            if (u) {
                try {
                    // A. ALLOWLIST CHECK
                    const allowRef = doc(db, 'config', 'auth', 'allowlist', u.email.toLowerCase());
                    const allowSnap = await getDoc(allowRef);

                    if (!allowSnap.exists()) {
                        console.warn("User not in allowlist, treating as LEAD:", u.email);
                        setUserRole('LEAD');
                        setUser(u);
                        setLoading(false);
                        return;
                    }

                    // B. GET ROLE
                    const role = allowSnap.data().role || 'CUSTOMER';
                    setUserRole(role);

                    // C. SYNC PROFILE
                    const userProfileRef = doc(db, 'artifacts', getAppId(), 'users', u.uid);
                    const userProfileSnap = await getDoc(userProfileRef);

                    if (!userProfileSnap.exists()) {
                        await setDoc(userProfileRef, {
                            email: u.email,
                            role: role,
                            created_at: new Date().toISOString(),
                            onboarding_complete: false
                        });
                        setShowOnboarding(true);
                    } else {
                        if (!userProfileSnap.data().onboarding_complete) setShowOnboarding(true);
                        if (userProfileSnap.data().role !== role) {
                            await setDoc(userProfileRef, { role: role }, { merge: true });
                        }
                    }

                    setUser(u);

                } catch (e) {
                    console.error("Auth Error:", e);
                    setNotification({ type: 'error', message: 'Login Fehler: ' + e.message });
                    await auth.signOut();
                }
            } else {
                setUser(null);
                setUserRole(null);
                setEffectiveUserId(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // LOAD ADMIN CLIENTS & EFFECTIVE COMPANY DATA
    useEffect(() => {
        // 1. Load Admin Clients if SUPER_ADMIN
        if (userRole === 'SUPER_ADMIN' && user) {
            const fetchClients = async () => {
                try {
                    const res = await callBackendFunction('admin/get_clients', { userId: user.uid });
                    if (res.success) setAdminClients(res.clients);
                } catch (e) {
                    console.error("Admin-Fehler beim Laden der Mandanten:", e);
                }
            };
            fetchClients();
        }

        // 2. Default Effective User ID to self
        if (user && !effectiveUserId) {
            setEffectiveUserId(user.uid);
        }
    }, [userRole, user]);

    useEffect(() => {
        if (!effectiveUserId) return;
        // Fetch company name for the masquerade badge
        const fetchCompany = async () => {
            try {
                const snap = await getDoc(doc(db, 'artifacts', getAppId(), 'users', effectiveUserId));
                if (snap.exists()) setCompanyData(snap.data());
            } catch (e) { console.error("Could not load company info"); }
        };
        // For performance, only load if we are masquerading
        if (effectiveUserId !== user?.uid) {
            fetchCompany();
        } else {
            setCompanyData(null);
        }
    }, [effectiveUserId, user]);

    const handleLogout = () => {
        auth.signOut();
        setView('dashboard');
    };

    const handleOnboardingComplete = (data) => {
        setShowOnboarding(false);
        setNotification({ type: 'success', message: 'Einrichtung abgeschlossen!' });
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><LoadingIndicator /></div>;

    if (view === 'candidate_assess') return <Suspense fallback={<LoadingIndicator />}><CandidateAssessmentView /></Suspense>;
    if (view === 'approve') return <Suspense fallback={<LoadingIndicator />}><ApprovalView /></Suspense>;
    if (view === 'dossier') {
        const params = new URLSearchParams(window.location.search);
        return <Suspense fallback={<LoadingIndicator />}><PublicDossierView companyId={params.get('c')} candidateId={params.get('id')} token={params.get('t')} /></Suspense>;
    }
    if (!user) return <Suspense fallback={<LoadingIndicator />}><LoginView /></Suspense>;
    if (userRole === 'LEAD') return <Suspense fallback={<LoadingIndicator />}><LeadView user={user} onLogout={handleLogout} /></Suspense>;

    if (view === 'business_admin' && userRole === 'SUPER_ADMIN') {
        return <Suspense fallback={<LoadingIndicator />}><BusinessDashboard user={user} setView={setView} /></Suspense>;
    }

    return (
        // PREMIUM: Changed bg-slate-100 to bg-stone-50 for a warmer, paper-like feel
        <div className="min-h-screen bg-stone-50 text-stone-900 font-sans selection:bg-indigo-100 selection:text-indigo-900">
            <NotificationToast notification={notification} setNotification={setNotification} />

            {showOnboarding && <MagicOnboardingView user={user} onComplete={handleOnboardingComplete} />}

            {/* ADMIN MASQUERADE BAR AROUND ENTIRE APP */}
            {userRole === 'SUPER_ADMIN' && effectiveUserId && (
                <div className="bg-amber-100 border-b border-amber-200 px-4 sm:px-6 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between shadow-inner sticky top-0 z-[60] gap-2">
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <span className="text-[9px] sm:text-[10px] whitespace-nowrap font-black text-amber-700 uppercase tracking-widest flex items-center gap-1 shrink-0">
                            Managed Mode
                        </span>
                        <select
                            value={effectiveUserId}
                            onChange={(e) => {
                                setEffectiveUserId(e.target.value);
                                setView('dashboard');
                            }}
                            className="bg-white border border-amber-300 text-[10px] sm:text-xs font-bold rounded px-2 py-1 outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer flex-grow sm:flex-none min-w-0"
                        >
                            <option value={user.uid}>Eigener Account (Fachkraft Kontor)</option>
                            {adminClients.map(client => (
                                <option key={client.id} value={client.id}>
                                    {client.company_name} ({client.email})
                                </option>
                            ))}
                        </select>
                    </div>
                    {effectiveUserId !== user.uid && (
                        <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto mt-1 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-amber-200">
                            <span className="px-2 py-0.5 rounded text-[9px] sm:text-[10px] bg-amber-200 text-amber-800 font-bold animate-pulse truncate max-w-[150px] sm:max-w-none">
                                Firma: {companyData?.company_name || '...'}
                            </span>
                            <button onClick={() => { setEffectiveUserId(user.uid); setView('dashboard'); }} className="text-[9px] sm:text-[10px] font-bold text-amber-600 hover:underline shrink-0">
                                Zurück
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Responsive Container: pt-4 on mobile, pt-8 on desktop */}
            <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
                <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><LoadingIndicator /></div>}>
                    {view === 'dashboard' && (
                        <DashboardView
                            user={user}
                            effectiveUserId={effectiveUserId}
                            setView={setView}
                            setSelectedJobId={setSelectedJobId}
                            handleLogout={handleLogout}
                            userRole={userRole}
                        />
                    )}
                    {view === 'creator' && <JobCreatorView setView={setView} user={user} effectiveUserId={effectiveUserId} setNotification={setNotification} setSelectedJobId={setSelectedJobId} />}
                    {view === 'detail' && <JobDetailView jobId={selectedJobId} user={user} effectiveUserId={effectiveUserId} setView={setView} setNotification={setNotification} />}
                </Suspense>
            </main>
        </div>
    );
}